/* eslint-disable prettier/prettier */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Chip,
  Paper,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  Place,
} from '@mui/icons-material';

const STATUS_COLORS = {
  aberto: '#2196f3',
  em_andamento: '#ff9800',
  aguardando_resposta: '#9c27b0',
  pausado: '#9e9e9e',
  resolvido: '#4caf50',
  fechado: '#f44336',
};

const PRIORIDADE_ICONS = {
  baixa: '●',
  media: '●',
  alta: '▲',
  urgente: '★',
};

/**
 * PlantaViewer - Componente para visualizar planta baixa com zoom, pan e marcadores
 *
 * Props:
 * - imagemUrl: URL da imagem da planta
 * - chamados: Array de chamados com planta_x/planta_y (para exibir marcadores)
 * - onClick: Callback (x%, y%) quando clicar na planta (modo de marcação)
 * - markerPosition: { x, y } posição do marcador atual (0-100%)
 * - editable: se true, permite clicar para marcar posição
 * - height: altura do container (default 500)
 * - selectedChamadoId: ID do chamado selecionado para destacar
 * - onMarkerClick: Callback chamado ao clicar em um marcador existente
 */
export default function PlantaViewer({
  imagemUrl,
  chamados = [],
  onClick,
  markerPosition,
  editable = false,
  height = 500,
  selectedChamadoId,
  onMarkerClick,
}) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);

  const handleZoomIn = useCallback(() => {
    setScale((s) => Math.min(s * 1.3, 8));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((s) => Math.max(s / 1.3, 0.3));
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.15 : 0.87;
    setScale((s) => Math.max(0.3, Math.min(8, s * delta)));
  }, []);

  // Mouse drag
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    // Se editável e não arrastando com ctrl/shift, tratar como clique de marcação
    setIsDragging(true);
    setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
  }, [translate]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    setTranslate({ x: dx, y: dy });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback((e) => {
    if (isDragging) {
      const movedX = Math.abs(e.clientX - (dragStart.x + translate.x));
      const movedY = Math.abs(e.clientY - (dragStart.y + translate.y));
      // Se movimento mínimo, tratar como clique
      if (movedX < 5 && movedY < 5 && editable && onClick) {
        const container = containerRef.current;
        if (container) {
          const img = container.querySelector('img');
          if (img) {
            const imgRect = img.getBoundingClientRect();
            const x = ((e.clientX - imgRect.left) / imgRect.width) * 100;
            const y = ((e.clientY - imgRect.top) / imgRect.height) * 100;
            if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
              onClick(x, y);
            }
          }
        }
      }
    }
    setIsDragging(false);
  }, [isDragging, dragStart, translate, editable, onClick]);

  // Touch support
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - translate.x, y: touch.clientY - translate.y });
    }
  }, [translate]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setTranslate({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  const handleImageLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  if (!imagemUrl) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height, bgcolor: '#f5f5f5', borderRadius: 2, border: '2px dashed #ccc' }}>
        <Typography color="text.secondary">Nenhuma planta cadastrada</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Controles de zoom */}
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Tooltip title="Zoom +" placement="left">
          <IconButton size="small" onClick={handleZoomIn}><ZoomIn /></IconButton>
        </Tooltip>
        <Tooltip title="Zoom -" placement="left">
          <IconButton size="small" onClick={handleZoomOut}><ZoomOut /></IconButton>
        </Tooltip>
        <Tooltip title="Resetar" placement="left">
          <IconButton size="small" onClick={handleReset}><CenterFocusStrong /></IconButton>
        </Tooltip>
      </Paper>

      {/* Info de zoom */}
      <Chip
        label={`${Math.round(scale * 100)}%`}
        size="small"
        sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10, bgcolor: 'rgba(255,255,255,0.9)' }}
      />

      {editable && (
        <Chip
          icon={<Place />}
          label="Clique na planta para marcar"
          color="primary"
          size="small"
          sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}
        />
      )}

      {/* Container da planta */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height,
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : (editable ? 'crosshair' : 'grab'),
          bgcolor: '#e8e8e8',
          borderRadius: 2,
          border: '1px solid #ccc',
          position: 'relative',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setIsDragging(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Box
          sx={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            position: 'relative',
            display: 'inline-block',
            width: '100%',
            height: '100%',
          }}
        >
          <img
            src={imagemUrl}
            alt="Planta Baixa"
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />

          {/* Marcadores de chamados existentes */}
          {loaded && chamados.map((ch) => (
            <Tooltip
              key={ch.id}
              title={`#${ch.id} - ${ch.titulo} (${ch.status})`}
              placement="top"
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (onMarkerClick) onMarkerClick(ch);
                }}
                sx={{
                  position: 'absolute',
                  left: `${ch.planta_x}%`,
                  top: `${ch.planta_y}%`,
                  transform: 'translate(-50%, -100%)',
                  cursor: 'pointer',
                  zIndex: 5,
                  filter: selectedChamadoId === ch.id ? 'drop-shadow(0 0 6px gold)' : 'none',
                  transition: 'filter 0.2s',
                  '&:hover': { filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' },
                }}
              >
                <Place
                  sx={{
                    fontSize: selectedChamadoId === ch.id ? 36 : 28,
                    color: STATUS_COLORS[ch.status] || '#2196f3',
                  }}
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    top: 2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: 9,
                    fontWeight: 'bold',
                    color: '#fff',
                    pointerEvents: 'none',
                  }}
                >
                  {PRIORIDADE_ICONS[ch.prioridade] || '●'}
                </Typography>
              </Box>
            </Tooltip>
          ))}

          {/* Marcador de posição selecionada atual */}
          {loaded && markerPosition && (
            <Box
              sx={{
                position: 'absolute',
                left: `${markerPosition.x}%`,
                top: `${markerPosition.y}%`,
                transform: 'translate(-50%, -100%)',
                zIndex: 6,
                animation: 'bounce 0.5s ease',
                '@keyframes bounce': {
                  '0%': { transform: 'translate(-50%, -150%)' },
                  '60%': { transform: 'translate(-50%, -90%)' },
                  '100%': { transform: 'translate(-50%, -100%)' },
                },
              }}
            >
              <Place sx={{ fontSize: 40, color: '#e91e63' }} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
